'use client';

import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import { getResponsiveAttributes } from '../utils';
import type { ResponsiveValue } from '../utils/types';
import styles from './heading.component.module.scss';

type AliasColor = 'brandColor' | 'white';
type Spacing = 4 | 8 | 10 | 12 | 16 | 20 | 24 | 32;

type HeadingToken =
  | 'heading-xs'
  | 'heading-s'
  | 'heading-m'
  | 'heading-l'
  | 'heading-xl'
  | 'heading-xxl'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5';

type Font = HeadingToken | ResponsiveValue<HeadingToken>;

type HeadingOwnProps = {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  color?: AliasColor;
  /**
   * タイポグラフィートークン
   */
  font?: Font;
  /**
   * text-align
   */
  align?: 'inherit' | 'left' | 'center' | 'right';
  /**
   * margin-top
   */
  mt?: Spacing;
  /**
   * margin-bottom
   */
  mb?: Spacing;
  /**
   * スクリーンリーダー用
   */
  srOnly?: boolean;
  children: ReactNode;
};

export type HeadingProps = HeadingOwnProps &
  Omit<ComponentPropsWithoutRef<'h1'>, 'color'>;

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  (props, ref) => {
    const {
      as: Root = 'h1',
      color,
      font = 'heading-xl',
      align,
      mt,
      mb,
      srOnly,
      children,
      className,
      ...rest
    } = props;

    return (
      <Root
        className={[className, styles.heading].filter(Boolean).join(' ')}
        data-color={color}
        data-mb={mb}
        data-mt={mt}
        data-sr-only={srOnly}
        data-text-align={align}
        ref={ref}
        {...getResponsiveAttributes('font', font)}
        {...rest}
      >
        {children}
      </Root>
    );
  }
);

Heading.displayName = 'Heading';
